import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { RefreshCw } from 'lucide-react'
import { IconLabel } from '@/components/ui/app-icon'
import { Modal } from '@/components/ui/modal'
import { Button } from '@/components/ui/button'
import type { TemplateData, PreviewTestData, LabelElement } from './types'
import { DEFAULT_TEST_DATA } from './types'
import { templatesApi } from '@/lib/api'
import { beginRenderHistory, finishRenderHistory } from '@/stores/renderHistory.store'
import { isAxiosError } from 'axios'

interface Props {
  isOpen: boolean
  onClose: () => void
  getTemplate: () => TemplateData
}

const PREVIEW_FIELD_LABELS: Record<keyof PreviewTestData, string> = {
  product_name: 'Название товара',
  product_code: 'Артикул',
  gtin: 'GTIN',
  km_code: 'КМ код',
  ka_code: 'КА код',
  task_id: 'ID задания',
  task_name: 'Название задания',
  task_comment: 'Комментарий задания',
  task_extra_attribute: 'Доп. реквизит',
  production_date: 'Дата производства',
  expiry_date: 'Срок годности',
  box_num: 'Номер коробки',
  pallet_num: 'Номер паллеты',
  codes_count: 'Количество упаковок',
  boxes_count: 'Количество коробок',
  sequence_number: 'Порядковый номер',
  first_sequence: 'Первый номер КМ',
  last_sequence: 'Последний номер КМ',
  sequence_range: 'Диапазон номеров КМ',
  first_box: 'Первая коробка',
  last_box: 'Последняя коробка',
  box_range: 'Диапазон коробок',
  km_in_box: 'КМ в коробке',
  km_on_pallet: 'КМ на паллете',
}

const PLACEHOLDER_RE = /\{\{([a-z_][a-z0-9_]*)\}\}|\{([a-z_][a-z0-9_]*)\}/g

const KNOWN_KEYS = new Set(Object.keys(DEFAULT_TEST_DATA) as (keyof PreviewTestData)[])

function defaultFieldForElement(el: LabelElement): string | undefined {
  if (el.field) return el.field
  switch (el.type) {
    case 'datamatrix':
      return 'km_code'
    case 'datamatrix_ka':
    case 'gs1_128':
      return 'ka_code'
    case 'ean13':
      return 'gtin'
    default:
      return undefined
  }
}

/** Keys from PreviewTestData that the template actually references. */
export function collectTemplatePreviewFields(template: TemplateData): (keyof PreviewTestData)[] {
  const used = new Set<keyof PreviewTestData>()
  for (const el of template.elements ?? []) {
    const field = defaultFieldForElement(el)
    if (field && KNOWN_KEYS.has(field as keyof PreviewTestData)) {
      used.add(field as keyof PreviewTestData)
    }
    if (el.label) {
      PLACEHOLDER_RE.lastIndex = 0
      let m: RegExpExecArray | null
      while ((m = PLACEHOLDER_RE.exec(el.label)) !== null) {
        const key = (m[1] || m[2]) as keyof PreviewTestData
        if (KNOWN_KEYS.has(key)) used.add(key)
      }
    }
  }
  return (Object.keys(DEFAULT_TEST_DATA) as (keyof PreviewTestData)[]).filter(k => used.has(k))
}

export function PreviewModal({ isOpen, onClose, getTemplate }: Props) {
  const [testData, setTestData] = useState<PreviewTestData>({ ...DEFAULT_TEST_DATA })
  const [previewSrc, setPreviewSrc] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [warnings, setWarnings] = useState<string[]>([])
  const [usedFields, setUsedFields] = useState<(keyof PreviewTestData)[]>([])

  const updateField = <K extends keyof PreviewTestData>(key: K, value: PreviewTestData[K]) => {
    setTestData(prev => ({ ...prev, [key]: value }))
  }

  const refreshUsedFields = useCallback(() => {
    setUsedFields(collectTemplatePreviewFields(getTemplate()))
  }, [getTemplate])

  const renderPreview = useCallback(async () => {
    setLoading(true)
    setError(null)
    setWarnings([])
    const tpl = getTemplate()
    setUsedFields(collectTemplatePreviewFields(tpl))
    const templateName =
      (tpl as { template_name?: string }).template_name?.trim() || tpl.name?.trim() || 'без имени'
    const trackId = beginRenderHistory('preview', templateName)
    try {
      const res = await templatesApi.renderPreview(
        tpl as unknown as Record<string, unknown>,
        testData as unknown as Record<string, unknown>,
      )
      const image = res.data?.image
      if (image) {
        setPreviewSrc(image)
        setWarnings(Array.isArray(res.data?.warnings) ? res.data.warnings : [])
        finishRenderHistory(trackId, 'ok')
      } else {
        setError('Ошибка генерации')
        finishRenderHistory(trackId, 'error', 'Пустой ответ')
      }
    } catch (err) {
      const msg = isAxiosError(err) ? err.message : 'Ошибка загрузки предпросмотра'
      setError(msg)
      setPreviewSrc(null)
      setWarnings([])
      finishRenderHistory(trackId, 'error', msg)
    } finally {
      setLoading(false)
    }
  }, [getTemplate, testData])

  const prevOpen = useRef(false)
  useEffect(() => {
    if (isOpen && !prevOpen.current) {
      refreshUsedFields()
      void renderPreview()
    }
    prevOpen.current = isOpen
  }, [isOpen, renderPreview, refreshUsedFields])

  const stringFields = useMemo(
    () => usedFields.filter(k => typeof DEFAULT_TEST_DATA[k] === 'string'),
    [usedFields],
  )
  const numberFields = useMemo(
    () => usedFields.filter(k => typeof DEFAULT_TEST_DATA[k] === 'number'),
    [usedFields],
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-5xl">
      <Modal.Header onClose={onClose}><IconLabel icon="preview">Предпросмотр этикетки</IconLabel></Modal.Header>
      <Modal.Body className="p-0">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x">
          <div className="flex items-center justify-center min-h-[300px] p-6 bg-gray-50">
            {loading ? (
              <div className="text-center space-y-2">
                <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto" />
                <p className="text-sm text-gray-500">Генерация этикетки...</p>
              </div>
            ) : error ? (
              <p className="text-red-500 text-sm text-center">{error}</p>
            ) : previewSrc ? (
              <div className="text-center space-y-2 w-full">
                <img src={previewSrc} alt="Предпросмотр" className="max-w-full max-h-[60vh] border shadow mx-auto" />
                <p className="text-xs text-gray-500">
                  Режим: {({
                    default: 'Общий (из Настроек)',
                    mono: 'Mono',
                    gray: 'Gray',
                    rgba: 'RGBA',
                  } as const)[getTemplate().render_mode || 'default']}
                </p>
                {warnings.length > 0 && (
                  <div className="text-left mx-auto max-w-md rounded border border-amber-300 bg-amber-50 px-3 py-2 space-y-1">
                    {warnings.map((w, i) => (
                      <p key={i} className="text-xs text-amber-900">{w}</p>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-400 text-sm">Нажмите «Обновить» для генерации</p>
            )}
          </div>

          <div className="p-4 overflow-y-auto max-h-[70vh] space-y-3">
            <h3 className="font-semibold text-sm text-gray-700"><IconLabel icon="sandbox">Тестовые данные</IconLabel></h3>

            {usedFields.length === 0 ? (
              <p className="text-xs text-gray-500">В шаблоне нет полей данных — только графика.</p>
            ) : (
              <>
                {stringFields.map(key =>
                  key === 'km_code' || key === 'ka_code' ? (
                    <div key={key}>
                      <label className="block text-xs text-gray-600 mb-0.5">{PREVIEW_FIELD_LABELS[key]}:</label>
                      <textarea
                        className="w-full border rounded px-2 py-1 text-sm font-mono"
                        rows={key === 'km_code' ? 2 : 1}
                        value={testData[key] as string}
                        onChange={e => updateField(key, e.target.value)}
                      />
                    </div>
                  ) : (
                    <Field
                      key={key}
                      label={`${PREVIEW_FIELD_LABELS[key]}:`}
                      value={testData[key] as string}
                      onChange={v => updateField(key, v)}
                    />
                  ),
                )}
                {numberFields.length > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    {numberFields.map(key => (
                      <NumField
                        key={key}
                        label={`${PREVIEW_FIELD_LABELS[key]}:`}
                        value={testData[key] as number}
                        onChange={v => updateField(key, v)}
                      />
                    ))}
                  </div>
                )}
              </>
            )}

            <Button variant="primary" className="w-full mt-3" onClick={renderPreview} isLoading={loading}>
              <RefreshCw className="h-4 w-4 mr-1" /> Обновить предпросмотр
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="block text-xs text-gray-600 mb-0.5">{label}</label>
      <input
        type="text"
        className="w-full border rounded px-2 py-1 text-sm"
        value={value}
        onChange={e => onChange(e.target.value)}
      />
    </div>
  )
}

function NumField({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <div>
      <label className="block text-xs text-gray-600 mb-0.5">{label}</label>
      <input
        type="number"
        className="w-full border rounded px-2 py-1 text-sm"
        value={value === 0 ? '' : String(value)}
        onChange={e => {
          const raw = e.target.value
          if (raw === '') {
            onChange(0)
          } else {
            const v = Number(raw)
            onChange(isNaN(v) ? 0 : v)
          }
        }}
      />
    </div>
  )
}
